// Per-field sizing shared by every place that draws a customer's text:
// the editor, the download page, and (a copy in admin/src/utils) the admin
// order editor. Keeping one implementation is what makes the download match
// what the customer saw.
//
// Each stored field value may carry `fontScale` (the customer's "Text size"
// slider, 0.5 - 2), `offsetX` / `offsetY` ("Change position": a fraction of
// the canvas width / height, so it means the same at any render size),
// `fill` ("Color": #rrggbb as the customer sees it) and may contain newlines
// ("Add a line").

export const SIZE_MIN = 50;
export const SIZE_MAX = 200;
export const SIZE_STEP = 5;

export function cleanScale(s) {
  const n = Number(s);
  if (!(n > 0)) return 1;
  return Math.min(SIZE_MAX / 100, Math.max(SIZE_MIN / 100, n));
}

// Position offset, clamped so a box can't be pushed entirely off the design.
export function cleanOffset(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.min(0.5, Math.max(-0.5, n)) * 10000) / 10000;
}

// '#abc', 'abc', '#aabbcc', 'aabbcc', 'rgb(r,g,b)' -> '#aabbcc'; else null.
export function cleanColor(v) {
  const str = String(v || '').trim();
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(str);
  if (rgb) return `#${rgb.slice(1, 4).map(n => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('')}`;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(str);
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1];
  return `#${h.toLowerCase()}`;
}

// Color themes hue-rotate the whole canvas (CSS filter on screen, ctx.filter
// in the export). A customer-picked text color would be rotated with it, so
// draw it pre-rotated by the exact inverse of the theme's hue-rotate matrix:
// after the filter it shows and prints as the color they picked.
function hueRotateMatrix(deg) {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [
    [0.213 + c * 0.787 - s * 0.213, 0.715 - c * 0.715 - s * 0.715, 0.072 - c * 0.072 + s * 0.928],
    [0.213 - c * 0.213 + s * 0.143, 0.715 + c * 0.285 + s * 0.140, 0.072 - c * 0.072 - s * 0.283],
    [0.213 - c * 0.213 - s * 0.787, 0.715 - c * 0.715 + s * 0.715, 0.072 + c * 0.928 + s * 0.072],
  ];
}

function invert3(m) {
  const [[a, b, c], [d, e, f], [g, h, i]] = m;
  const A = e * i - f * h; const B = -(d * i - f * g); const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-9) return null;
  return [
    [A / det, -(b * i - c * h) / det, (b * f - c * e) / det],
    [B / det, (a * i - c * g) / det, -(a * f - c * d) / det],
    [C / det, -(a * h - b * g) / det, (a * e - b * d) / det],
  ];
}

export function fillUnderFilter(hex, filter) {
  const color = cleanColor(hex);
  const m = /hue-rotate\((-?[\d.]+)deg\)/.exec(filter || '');
  if (!color || !m || !Number(m[1])) return color;
  const inv = invert3(hueRotateMatrix(Number(m[1])));
  if (!inv) return color;
  const rgb = [1, 3, 5].map(i => parseInt(color.slice(i, i + 2), 16) / 255);
  return `#${inv.map(row => {
    const v = row[0] * rgb[0] + row[1] * rgb[1] + row[2] * rgb[2];
    return Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0');
  }).join('')}`;
}

// "Add a line" leaves an empty last line until the customer types in it;
// it is not drawn.
export function textForRender(text) {
  return String(text == null ? '' : text).replace(/\s*\n\s*$/, '');
}

// Box of a field at the customer's size, in display pixels (ds = display
// scale). Single-line fields shrink to fit their box, so when the customer
// enlarges the text the box widens around its centre (never wider than the
// canvas) to let the text actually grow. Multi-line fields keep their box and
// wrap. `offset` ({ x, y } fractions) moves the box by that share of the
// canvas size (canvasW / canvasH, display pixels).
export function fieldBox(f, scale, ds, canvasW, canvasH, offset) {
  const baseW = (f.width || f.maxWidth || 200) * ds;
  let left = (f.left || 0) * ds;
  let top = (f.top || 0) * ds;
  let width = baseW;
  if (!f.multiLine && scale > 1) {
    const sx = f.scaleX || 1;
    const cap = canvasW ? (canvasW * 0.96) / sx : Infinity;
    width = Math.max(baseW, Math.min(baseW * scale, cap));
    const grow = (width - baseW) * sx;
    const ox = f.originX || 'left';
    const shift = ox === 'center' ? 0 : ox === 'right' ? grow / 2 : -grow / 2;
    const a = ((f.angle || 0) * Math.PI) / 180;
    left += shift * Math.cos(a);
    top += shift * Math.sin(a);
  }
  if (offset && canvasW && canvasH) {
    left += cleanOffset(offset.x) * canvasW;
    top += cleanOffset(offset.y) * canvasH;
  }
  return { left, top, width };
}

// Largest font size (stepping down from baseSize) at which every line of a
// single-line field fits in boxWidth.
export function fitSingleLineSize(text, baseSize, boxWidth, fontFamily, charSpacing) {
  const ctx = document.createElement('canvas').getContext('2d');
  const lines = String(text || '').split('\n');
  const measure = (size) => {
    ctx.font = `${size}px "${fontFamily || 'Nexa'}"`;
    let widest = 0;
    for (const line of lines) {
      let w = ctx.measureText(line).width;
      if (charSpacing && line.length > 1) w += (charSpacing / 1000) * size * (line.length - 1);
      if (w > widest) widest = w;
    }
    return widest;
  };
  let size = baseSize;
  let guard = 400;
  while (measure(size) > boxWidth - 2 && size > 4 && guard-- > 0) size -= 0.5;
  return size;
}
